import React, { useCallback, useEffect, useState } from 'react';
import HelpAsk from '../../help/HelpAsk';

// Padlock toggle + unlock-prompt modal shared by Weekly Schedule and Weekly
// Attendance. The grid is locked by default to prevent stray clicks from
// creating sessions / attendance records. Clicks on existing items are
// always allowed; clicks on empty grid cells are intercepted by the host
// component which calls promptUnlock() to show the modal.

interface ToggleProps {
  locked: boolean;
  onToggle: () => void;
  /** Bumped by the lock guard on a single stray click. The toggle reacts
   *  with a brief single-pulse so the user notices the lock without being
   *  interrupted by a modal. */
  flashKey?: number;
}

export const GridLockToggle: React.FC<ToggleProps> = ({ locked, onToggle, flashKey = 0 }) => {
  const [flashing, setFlashing] = useState(false);
  useEffect(() => {
    if (flashKey === 0) return;
    setFlashing(true);
    // Match the SCSS `.is-flashing` animation duration (0.5s × 2 passes)
    // so the class sticks just long enough for the keyframes to play
    // out, then drops off cleanly.
    const t = setTimeout(() => setFlashing(false), 1000);
    return () => clearTimeout(t);
  }, [flashKey]);

  return (
    <button
      type="button"
      onClick={onToggle}
      onMouseDown={(e) => e.stopPropagation()}
      className={`grid-lock-toggle ${locked ? 'is-locked' : 'is-unlocked'} ${flashing ? 'is-flashing' : ''}`}
      title={locked ? 'Grid locked — click to unlock for editing' : 'Grid unlocked — click to lock'}
      aria-pressed={!locked}
      aria-label={locked ? 'Unlock grid' : 'Lock grid'}
    >
      <i className={`fas ${locked ? 'fa-lock' : 'fa-lock-open'}`} aria-hidden />
      <span className="grid-lock-toggle__label">{locked ? 'Locked' : 'Unlocked'}</span>
    </button>
  );
};

// ── Click-guard hook for locked grids ────────────────────────────────────
// Returns a `flashKey` to feed into <GridLockToggle> and a `guard` callback
// that the host component invokes when the grid is locked and the user
// clicks somewhere editable. The guard interprets the gesture:
//   • Single click            → flashes the lock pill (no modal).
//   • Native double-click     → onConfirmedAttempt() (opens unlock modal).
//   • Click + drag (>4px)     → onConfirmedAttempt().
// Single clicks never escalate to the modal — the user must explicitly
// double-click or drag, which keeps the unlock prompt out of the way of
// stray taps.
export function useLockedClickGuard(onConfirmedAttempt: () => void) {
  const [flashKey, setFlashKey] = useState(0);

  const guard = useCallback((e: React.MouseEvent) => {
    // Native double-click — OS-level repeat detection.
    if (e.detail >= 2) {
      onConfirmedAttempt();
      return;
    }

    // Single click — flash the lock pill.
    setFlashKey(k => k + 1);

    // Click-and-drag detection: if the mouse moves >4px before this click
    // ends, treat it as a deliberate gesture and surface the modal.
    const startX = e.clientX;
    const startY = e.clientY;
    let resolved = false;
    const onMove = (ev: MouseEvent) => {
      if (resolved) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (dx * dx + dy * dy > 16) {
        resolved = true;
        cleanup();
        onConfirmedAttempt();
      }
    };
    const onUp = () => { resolved = true; cleanup(); };
    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [onConfirmedAttempt]);

  return { flashKey, guard };
}

interface PromptProps {
  open: boolean;
  /** What the user was trying to do — used for the modal copy. */
  intent: 'create-session' | 'create-attendance';
  onUnlock: () => void;
  onCancel: () => void;
}

export const UnlockPromptModal: React.FC<PromptProps> = ({ open, intent, onUnlock, onCancel }) => {
  if (!open) return null;

  const title =
    intent === 'create-session'
      ? 'Unlock the schedule grid?'
      : 'Unlock the attendance grid?';
  const message =
    intent === 'create-session'
      ? 'The schedule grid is locked to prevent accidental changes. Unlock to add, move or resize sessions.'
      : 'The attendance grid is locked to prevent accidental new records. Unlock to record attendance for empty sessions.';

  // Bootstrap-modal shape, mirroring ConfirmationModal so the editor lock
  // dialog reads consistently with delete-confirm and other prompts. The
  // padlock theming is intentionally muted — green is reserved elsewhere
  // for success/confirm semantics.
  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="grid-unlock-title"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div
          className="modal-content ivory-panel"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="modal-header" style={{ position: 'relative', zIndex: 1 }}>
            <h5
              id="grid-unlock-title"
              className="modal-title d-flex align-items-center gap-2"
              style={{ fontWeight: 600 }}
            >
              <i className="fas fa-lock" style={{ color: '#9a6f0f', fontSize: '0.95rem' }} aria-hidden />
              {title}
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onCancel}
            />
          </div>
          <div className="modal-body" style={{ position: 'relative', zIndex: 1 }}>
            <p style={{ margin: 0 }}>{message}</p>
            {intent === 'create-session' && (
              <div className="mt-2">
                <HelpAsk
                  guideKey="schedule.change-session-times"
                  label="How do I change a session?"
                  variant="inline"
                />
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ position: 'relative', zIndex: 1 }}>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={onUnlock}
            >
              <i className="fas fa-lock-open" aria-hidden />
              Unlock
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
